import React, { Fragment, useState } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import moment from 'moment';
import * as Yup from 'yup';

import {
  createAmenityCalendarHold,
  getCalendarHolds,
  cancelCalendarHold,
} from 'actions/landlordPropertiesActions';

import { Formik, Form as FormikForm } from 'formik';
import Select from 'components/ui/SelectField';
import CtaText from 'components/ui/CtaCard';
import Form from 'components/ui/Form';
import Header from 'components/shared/Header';
import FlatButton from '@material-ui/core/Button';
import { ButtonPrimary } from '@yuhu-inc/yuhui';
import DialogActions from '@material-ui/core/DialogActions';
import DialogTitle from '@material-ui/core/DialogTitle';
import Dialog from '@material-ui/core/Dialog';
import DeleteIcon from 'assets/blue_trash_icon.png';
import DatePicker from 'components/ui/DatePicker';
import CheckboxNormal from 'components/ui/CheckboxNormal';
import withMobileDialog from '@material-ui/core/withMobileDialog';

import { media } from 'helpers/MediaHelper';
import { colors } from 'helpers/ColorHelper';
import { createTimeRange, toFriendlyDateString, toFriendlyTimeString } from 'helpers/Formatter';
import { getTranslatedString, getCurrentLocale } from 'helpers/i18n';
import { AccentColor } from 'helpers/AccentColorHelper';
import {
  combineDateAndTime,
  toEndOfDay,
  toISO8601DateTimeString,
} from '../../../../../helpers/Formatter';

const StyledButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  padding-bottom: 17px;
`;

const StyledScheduledHoldContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 10px;
  margin: 20px 10px;
`;

const StyledFormikForm = styled(FormikForm)`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
`;

const StyledDialogActions = styled(DialogActions)`
  justify-content: space-between;
  margin: 20px;
`;

const StyledDialogBody = styled.div`
  display: flex;
  flex-direction: column;
  margin: 24px;
`;

const StyledRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 15px;
  ${media.mobile`
    flex-direction: column;
    margin-bottom: 0px;
  `};
`;

const StyledRowItem = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0px 5px;
  width: ${(props) => props.width || 'auto'};
  ${media.mobile`
    width: auto;
    margin-bottom: 15px;
  `};
`;

const StyledTextArea = styled.textarea`
  width: 100%;
  resize: none;
  overflow-y: hidden;
  border: none;
`;

const StyledTextAreaContainer = styled.div`
  border-radius: 8px;
  border: 1px solid #d9d9e3;
  padding: 8px 5px;
`;

const StyledTOVerbiage = styled.div`
  font-size: 16px;
  margin-top: 30px;
  ${media.mobile`
    margin: 5px 0px 0px;
    text-align: center;
  `};
`;

const StyledLabel = styled.div``;

const StyledError = styled.div`
  color: ${colors.YUHU_RED};
`;

const StyledScheduledHold = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 10px;
`;

const StyledScheduledHoldVerbiageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 80%;
`;

const StyledScheduledStartEndTimeVerbiageH5 = styled.h5`
  margin: 0px;
  font-size: 17px;
`;
const StyledScheduledUntilVerbiageH5 = styled.h5`
  margin: 0px;
  font-size: 17px;
`;
const StyledScheduledHoldDescription = styled.p`
  max-width: 100%;
  overflow: hidden;
  margin: 0px;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const AmenityCalendarHoldSchema = Yup.object().shape({
  all_day_hold: Yup.boolean(),
  start_date: Yup.string().required(getTranslatedString('required')).nullable(),
  start_time: Yup.string().when('all_day_hold', {
    is: false,
    then: Yup.string().required(getTranslatedString('required')),
  }),
  end_date: Yup.string().required(getTranslatedString('required')),
  end_time: Yup.string().when('all_day_hold', {
    is: false,
    then: Yup.string().required(getTranslatedString('required')),
  }),
  repeat_type: Yup.string(),
  repeat_until: Yup.string().when('repeat_type', {
    is: (val) => ['daily', 'weekly', 'monthly', 'weekday', 'weekend'].includes(val),
    then: Yup.string().required(getTranslatedString('required')),
  }),
  description: Yup.string().required(getTranslatedString('required')),
});

const AmenityCalendarHold = ({
  amenity,
  createAmenityCalendarHold,
  calendarHolds,
  getCalendarHolds,
  cancelCalendarHold,
}) => {
  const [calendarHoldToggle, setCalendarHoldToggle] = useState(false);

  return (
    <Fragment>
      <Header
        name={getTranslatedString('landlordPortal.amenities.calendarHolds.header').toUpperCase()}
      />
      <div className="row" style={{ padding: '15px' }}>
        <p style={{ textAlign: 'justify' }}>
          {getTranslatedString('landlordPortal.amenities.calendarHolds.details')}
        </p>
      </div>
      <StyledButtonContainer>
        <FlatButton
          hoverColor="grey"
          style={{ backgroundColor: AccentColor(), color: 'white' }}
          onClick={() => setCalendarHoldToggle(!calendarHoldToggle)}
        >
          {getTranslatedString('landlordPortal.amenities.calendarHolds.button.addNew')}
        </FlatButton>
      </StyledButtonContainer>
      {calendarHolds && calendarHolds.length > 0 && (
        <Fragment>
          <Header
            name={getTranslatedString(
              'landlordPortal.amenities.calendarHolds.scheduled.header'
            ).toUpperCase()}
          />
          <StyledScheduledHoldContainer>
            {calendarHolds &&
              calendarHolds.map((calendarHold) => (
                <ScheduledHold
                  calendarHold={calendarHold}
                  cancelCalendarHold={cancelCalendarHold}
                  getCalendarHolds={getCalendarHolds}
                />
              ))}
          </StyledScheduledHoldContainer>
        </Fragment>
      )}

      <CalendarHoldForm
        amenity={amenity}
        createAmenityCalendarHold={createAmenityCalendarHold}
        calendarHoldToggle={calendarHoldToggle}
        setCalendarHoldToggle={setCalendarHoldToggle}
        getCalendarHolds={getCalendarHolds}
      />
    </Fragment>
  );
};

export default withMobileDialog()(
  connect((store) => ({ calendarHolds: store.landlordProperties.calendarHolds }), {
    createAmenityCalendarHold,
    getCalendarHolds,
    cancelCalendarHold,
  })(AmenityCalendarHold)
);

const DeleteCalendarHoldDialog = ({
  toggleDeleteDialog,
  setToggleDeleteDialog,
  fullScreen,
  calendarHold,
  startEndTimeVerbiage,
  cancelCalendarHold,
  getCalendarHolds,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  return (
    <Dialog
      disableBackdropClick
      fullScreen={fullScreen}
      open={toggleDeleteDialog}
      onClose={() => setToggleDeleteDialog(!toggleDeleteDialog)}
      aria-labelledby="delete-calendar-hold"
      classes={{ paper: 'form-dialog' }}
    >
      <DialogTitle id="add-new-hold">
        <h3 style={{ textAlign: 'center', margin: '0px 0px 10px', color: '#4e4e4e' }}>
          {getTranslatedString('landlordPortal.amenities.calendarHolds.areYouSureToRemove')}
        </h3>
        <p style={{ textAlign: 'center', margin: 0, fontSize: '14px', color: '#4e4e4e' }}>
          {getTranslatedString('landlordPortal.amenities.calendarHolds.removingCalendarHold', {
            startEndTimeVerbiage: startEndTimeVerbiage,
          })}
        </p>
      </DialogTitle>

      <StyledDialogActions>
        <CtaText type="button" onClick={() => setToggleDeleteDialog(!toggleDeleteDialog)}>
          {getTranslatedString('landlordPortal.amenities.form.cancel')}
        </CtaText>
        <ButtonPrimary
          type="button"
          disabled={isDeleting}
          contentKey="landlordPortal.amenities.calendarHolds.removeUpperCase"
          onClick={() => {
            setIsDeleting(true);
            cancelCalendarHold(calendarHold.id).then(() => {
              setIsDeleting(false);
              getCalendarHolds(calendarHold.property_amenity_id);
              setToggleDeleteDialog(!toggleDeleteDialog);
            });
          }}
        />
      </StyledDialogActions>
    </Dialog>
  );
};

const ScheduledHold = ({ calendarHold, cancelCalendarHold, getCalendarHolds }) => {
  const [toggleDeleteDialog, setToggleDeleteDialog] = useState(false);

  const repeatType = getTranslatedString(
    `landlordPortal.amenities.calendarHolds.${calendarHold.repeat_type}`
  );
  let startTime;
  let endTime;
  let startEndTimeVerbiage;
  let startDate = toFriendlyDateString(calendarHold.start_at);
  let endDate = toFriendlyDateString(calendarHold.end_at);
  if (calendarHold.all_day_hold) {
    startEndTimeVerbiage = `${startDate} - ${endDate} (${repeatType})`;
  } else {
    startTime = toFriendlyTimeString(calendarHold.start_at);
    endTime = toFriendlyTimeString(calendarHold.end_at);
    startEndTimeVerbiage = `${startDate} ${startTime} - ${endTime} ${endDate} (${repeatType})`;
  }

  const repeatUntil =
    calendarHold.repeat_until && calendarHold.repeat_type !== 'never'
      ? toFriendlyDateString(calendarHold.repeat_until)
      : null;
  const repeatUntilVerbiage = repeatUntil
    ? getTranslatedString('landlordPortal.amenities.calendarHolds.repeatsUntilWithDate', {
        repeatUntil: repeatUntil,
      })
    : null;

  return (
    <Fragment>
      <StyledScheduledHold>
        <StyledScheduledHoldVerbiageContainer>
          <StyledScheduledStartEndTimeVerbiageH5>
            {startEndTimeVerbiage}
          </StyledScheduledStartEndTimeVerbiageH5>
          <StyledScheduledUntilVerbiageH5>{repeatUntilVerbiage}</StyledScheduledUntilVerbiageH5>
          <StyledScheduledHoldDescription>
            {calendarHold.description}
          </StyledScheduledHoldDescription>
        </StyledScheduledHoldVerbiageContainer>
        <img
          src={DeleteIcon}
          style={{ cursor: 'pointer' }}
          onClick={() => setToggleDeleteDialog(true)}
          size={24}
          alt=""
        />
      </StyledScheduledHold>

      <DeleteCalendarHoldDialog
        toggleDeleteDialog={toggleDeleteDialog}
        setToggleDeleteDialog={setToggleDeleteDialog}
        calendarHold={calendarHold}
        startEndTimeVerbiage={startEndTimeVerbiage}
        cancelCalendarHold={cancelCalendarHold}
        getCalendarHolds={getCalendarHolds}
      />
    </Fragment>
  );
};

const CalendarHoldForm = ({
  calendarHoldToggle,
  setCalendarHoldToggle,
  amenity,
  createAmenityCalendarHold,
  fullScreen,
  getCalendarHolds,
}) => {
  const [allDayHold, setAllDayHold] = useState(false);
  const [minEndDate, setMinEndDate] = useState(moment().startOf('day'));
  const [minRepeatUntilDate, setMinRepeatUntilDate] = useState(moment().startOf('day'));
  const [invalidStartEndTime, setInvalidStartEndTime] = useState(false);
  const [repeatType, setRepeatType] = useState('never');

  const timeRange = createTimeRange();

  const repeat_types = [
    {
      value: 'never',
      label: getTranslatedString('landlordPortal.amenities.calendarHolds.never'),
    },
    {
      value: 'daily',
      label: getTranslatedString('landlordPortal.amenities.calendarHolds.daily'),
    },
    {
      value: 'weekly',
      label: getTranslatedString('landlordPortal.amenities.calendarHolds.weekly'),
    },
    {
      value: 'weekday',
      label: getTranslatedString('landlordPortal.amenities.calendarHolds.weekdayLabel'),
    },
    {
      value: 'weekend',
      label: getTranslatedString('landlordPortal.amenities.calendarHolds.weekendLabel'),
    },
  ];

  const repeats_until_types = ['daily', 'weekly', 'weekday', 'weekend'];
  const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
  const weekend = ['Saturday', 'Sunday'];

  return (
    <Dialog
      disableEnforceFocus
      disableBackdropClick
      fullScreen={fullScreen}
      open={calendarHoldToggle}
      onClose={() => setCalendarHoldToggle(!calendarHoldToggle)}
      aria-labelledby="add-calendar-hold"
      classes={{ paper: 'form-dialog' }}
    >
      <Formik
        initialValues={{
          start_date: '',
          start_time: '',
          end_date: '',
          end_time: '',
          repeat_type: 'never',
          repeat_until: '',
          description: '',
          all_day_hold: false,
        }}
        onSubmit={(values, { resetForm }) => {
          let startAt;
          let endAt;
          if (values.all_day_hold) {
            startAt = toISO8601DateTimeString(values.start_date);
            endAt = toISO8601DateTimeString(values.end_date);
          } else {
            startAt = toISO8601DateTimeString(
              combineDateAndTime(values.start_date, values.start_time)
            );
            endAt = toISO8601DateTimeString(combineDateAndTime(values.end_date, values.end_time));
          }
          const formData = {
            property_id: amenity.property_for_multiselect.find(
              (v) => v.label === amenity.property_name
            )?.value,
            property_amenity_id: amenity.id,
            description: values.description,
            start_at: startAt,
            end_at: endAt,
            repeat_type: values.repeat_type,
            repeat_until: values.repeat_until
              ? toISO8601DateTimeString(toEndOfDay(values.repeat_until))
              : null,
            all_day_hold: values.all_day_hold,
          };
          createAmenityCalendarHold(formData).then(() => {
            resetForm();
            getCalendarHolds(amenity.id);
            setAllDayHold(false);
            setCalendarHoldToggle(!calendarHoldToggle);
          });
        }}
        validationSchema={AmenityCalendarHoldSchema}
      >
        {({ values, setFieldValue, errors, submitForm, isSubmitting }) => {
          const handleAllDayHoldChange = (event) => {
            setAllDayHold(!allDayHold);
            setFieldValue('all_day_hold', event.target.checked);
            if (event.target.checked) {
              setFieldValue('start_time', '');
              setFieldValue('end_time', '');
            }
          };

          const validateStartEndTimes = (values) => {
            if (!values.start_time || !values.end_time || !values.start_date || !values.end_date)
              return false;

            if (values.start_date === values.end_date) {
              let startTime = moment(values.start_time, 'HH:mm a');
              let endTime = moment(values.end_time, 'HH:mm a');

              let isValid = startTime.isBefore(endTime);
              setInvalidStartEndTime(!isValid);
              return isValid;
            }
            setInvalidStartEndTime(false);
            return true;
          };

          const disableDate = (date, minDate) => {
            if (repeatType === 'weekend') {
              return weekdays.includes(date.format('dddd')) || date < minDate;
            }
            if (repeatType === 'weekday') {
              return weekend.includes(date.format('dddd')) || date < minDate;
            }

            return date < minDate;
          };

          return (
            <Fragment>
              <StyledFormikForm>
                <DialogTitle id="add-new-hold">
                  <h3 style={{ textAlign: 'center', margin: '0px 0px 10px', color: '#4e4e4e' }}>
                    {getTranslatedString('landlordPortal.amenities.calendarHolds.dialog.header')}
                  </h3>
                  <p style={{ textAlign: 'center', margin: 0, fontSize: '14px', color: '#4e4e4e' }}>
                    {getTranslatedString('landlordPortal.amenities.calendarHolds.dialog.details')}
                  </p>
                </DialogTitle>
                <StyledDialogBody>
                  <StyledRow>
                    <StyledRowItem width={allDayHold ? '100%' : 'auto'}>
                      <StyledLabel>
                        {getTranslatedString('landlordPortal.amenities.calendarHolds.date')}
                      </StyledLabel>
                      <Form.Item>
                        <DatePicker
                          name="start_date"
                          value={values.start_date}
                          placeholder="0000-00-00"
                          mobileMode="date"
                          showToday={false}
                          defaultPickerValue={moment().locale(getCurrentLocale())}
                          disabledDate={(current) => {
                            return disableDate(current, moment().startOf('day'));
                          }}
                          style={{ width: '100%' }}
                          onChange={(value) => {
                            const updatedValues = values;
                            setMinEndDate(value);
                            setMinRepeatUntilDate(value);
                            setFieldValue('start_date', value);
                            setFieldValue('end_date', '');
                            setFieldValue('repeat_until', '');
                            updatedValues.start_date = value;
                            validateStartEndTimes(updatedValues);
                          }}
                        />
                      </Form.Item>
                      {errors.start_date && <StyledError>{errors.start_date}</StyledError>}
                    </StyledRowItem>

                    {!allDayHold && (
                      <StyledRowItem width="25%">
                        <StyledLabel>
                          {getTranslatedString('landlordPortal.amenities.calendarHolds.time')}
                        </StyledLabel>
                        <Form.Item>
                          <Select
                            name="start_time"
                            placeholder={getTranslatedString(
                              'landlordPortal.purchaseOrderForm.select'
                            )}
                            value={values.start_time}
                            onChange={(value) => {
                              const updatedValues = values;
                              setFieldValue('start_time', value);
                              updatedValues.start_time = value;
                              validateStartEndTimes(updatedValues);
                            }}
                          >
                            {timeRange.map((time, i) => (
                              <Select.Option value={time} key={`start_time_${time}`}>
                                {time}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                        {errors.start_time && <StyledError>{errors.start_time}</StyledError>}
                      </StyledRowItem>
                    )}

                    <StyledRowItem>
                      <StyledTOVerbiage>
                        {getTranslatedString('landlordPortal.amenities.calendarHolds.toUpperCase')}
                      </StyledTOVerbiage>
                    </StyledRowItem>

                    {!allDayHold && (
                      <StyledRowItem width="25%">
                        <StyledLabel>
                          {getTranslatedString('landlordPortal.amenities.calendarHolds.time')}
                        </StyledLabel>
                        <Form.Item>
                          <Select
                            name="end_time"
                            placeholder={getTranslatedString(
                              'landlordPortal.purchaseOrderForm.select'
                            )}
                            value={values.end_time}
                            onChange={(value) => {
                              const updatedValues = values;
                              setFieldValue('end_time', value);
                              updatedValues.end_time = value;
                              validateStartEndTimes(updatedValues);
                            }}
                          >
                            {timeRange.map((time, i) => (
                              <Select.Option value={time} key={`end_time${time}`}>
                                {time}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                        {errors.end_time && <StyledError>{errors.end_time}</StyledError>}
                      </StyledRowItem>
                    )}

                    <StyledRowItem width={allDayHold ? '100%' : 'auto'}>
                      <StyledLabel>
                        {getTranslatedString('landlordPortal.amenities.calendarHolds.date')}
                      </StyledLabel>
                      <Form.Item>
                        <DatePicker
                          name="end_date"
                          value={values.end_date}
                          placeholder="0000/00/00"
                          mobileMode="date"
                          defaultPickerValue={moment().locale(getCurrentLocale())}
                          disabledDate={(current) => disableDate(current, minEndDate)}
                          style={{ width: '100%' }}
                          onChange={(value) => {
                            const updatedValues = values;
                            setMinRepeatUntilDate(value);
                            setFieldValue('end_date', value);
                            setFieldValue('repeat_until', '');
                            updatedValues.end_date = value;
                            validateStartEndTimes(updatedValues);
                          }}
                        />
                      </Form.Item>
                      {errors.end_date && <StyledError>{errors.end_date}</StyledError>}
                    </StyledRowItem>
                  </StyledRow>
                  <StyledRow>
                    <StyledRowItem>
                      {invalidStartEndTime && (
                        <StyledError>
                          {getTranslatedString(
                            'landlordPortal.amenities.calendarHolds.invalidTimes'
                          )}
                        </StyledError>
                      )}
                    </StyledRowItem>
                  </StyledRow>
                  <StyledRow>
                    <StyledRowItem width="68%">
                      <StyledLabel>
                        {getTranslatedString('landlordPortal.amenities.calendarHolds.repeats')}
                      </StyledLabel>
                      <Select
                        showSearch
                        placeholder={getTranslatedString('landlordPortal.purchaseOrderForm.select')}
                        size="large"
                        name="repeat_type"
                        value={values.repeat_type}
                        onChange={(value) => {
                          if (value === 'never') {
                            setFieldValue('repeat_until', '');
                          }
                          if (value === 'weekday' || value === 'weekend') {
                            const updatedValues = values;
                            setFieldValue('start_date', '');
                            setFieldValue('end_date', '');
                            setFieldValue('repeat_until', '');
                            updatedValues.start_date = '';
                            updatedValues.end_date = '';
                            updatedValues.repeat_until = '';
                            validateStartEndTimes(updatedValues);
                          }
                          setRepeatType(value);
                          setFieldValue('repeat_type', value);
                        }}
                        filterOption={(input, option) =>
                          option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                      >
                        {repeat_types.map((repeat_type, i) => (
                          <Select.Option
                            value={repeat_type.value}
                            key={`${repeat_type.value}_${i}`}
                          >
                            {repeat_type.label}
                          </Select.Option>
                        ))}
                      </Select>
                    </StyledRowItem>
                    <StyledRowItem>
                      <CheckboxNormal
                        name="all_day_hold"
                        checked={allDayHold}
                        onChange={(e) => handleAllDayHoldChange(e)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          height: '100%',
                          marginTop: '15px',
                        }}
                      >
                        {getTranslatedString('landlordPortal.amenities.calendarHolds.allDayHold')}
                      </CheckboxNormal>
                    </StyledRowItem>
                  </StyledRow>

                  {repeats_until_types.includes(values.repeat_type) && (
                    <StyledRow>
                      <StyledRowItem width="100%">
                        <StyledLabel>
                          {getTranslatedString(
                            'landlordPortal.amenities.calendarHolds.repeatsUntil'
                          )}
                        </StyledLabel>
                        <Form.Item>
                          <DatePicker
                            size="large"
                            name="repeat_unit"
                            value={values.repeat_until}
                            placeholder="0000/00/00"
                            mobileMode="date"
                            showToday={false}
                            defaultPickerValue={moment().locale(getCurrentLocale())}
                            disabledDate={(current) => disableDate(current, minRepeatUntilDate)}
                            onChange={(value) => setFieldValue('repeat_until', value)}
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                        {errors.repeat_until && <StyledError>{errors.repeat_until}</StyledError>}
                      </StyledRowItem>
                    </StyledRow>
                  )}

                  <StyledRow>
                    <StyledRowItem width="100%">
                      <StyledLabel>
                        {getTranslatedString('landlordPortal.amenities.calendarHolds.description')}
                      </StyledLabel>
                      <StyledTextAreaContainer>
                        <StyledTextArea
                          value={values.description}
                          rows="3"
                          onChange={(event) =>
                            setFieldValue('description', event.currentTarget.value)
                          }
                        />
                      </StyledTextAreaContainer>
                      {errors.description && <StyledError>{errors.description}</StyledError>}
                    </StyledRowItem>
                  </StyledRow>
                </StyledDialogBody>
                <StyledDialogActions>
                  <CtaText
                    type="button"
                    onClick={() => {
                      setAllDayHold(false);
                      setRepeatType('never');
                      setMinEndDate(moment().startOf('day'));
                      setMinRepeatUntilDate(moment().startOf('day'));
                      setInvalidStartEndTime(false);
                      setCalendarHoldToggle(!calendarHoldToggle);
                    }}
                  >
                    {getTranslatedString('landlordPortal.amenities.form.cancel')}
                  </CtaText>
                  <ButtonPrimary
                    type="button"
                    disabled={isSubmitting}
                    contentKey="landlordPortal.amenities.calendarHolds.dialog.continue"
                    onClick={() => submitForm()}
                  />
                </StyledDialogActions>
              </StyledFormikForm>
            </Fragment>
          );
        }}
      </Formik>
    </Dialog>
  );
};
